import { I18nThemed } from 'interfaces/mixins';
import { w } from '@dojo/framework/core/vdom';
import { DNode, WidgetProperties } from '@dojo/framework/core/interfaces';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { Messages } from '@dojo/framework/i18n/i18n';
import has from '@dojo/framework/core/has';

import { QuestionTypeProperties } from '../interfaces';
import { MCQuestionBase, MCQuestionProperties } from './mc-base';
import { MCQuestionPretest } from './mc-pretest';
import { MCQuestionPrometric } from './mc-prometric';
import { MCQuestionPearsonVue } from './mc-pearsonvue';
import { MCQuestionAudit } from './mc-audit';

import { PrometricReview } from './review-prometric';
import { PrometricMPEReview } from './review-prometric-mpe';
import { PearsonVueReview } from './review-pearsonvue';
import { PretestReview } from './review-pretest';

export interface MCSelectorProperties extends QuestionTypeProperties {
	quizkind: string;
	progression: string;
	inreview?: boolean;
	isaudit?: boolean;
}

export class MCSelector extends I18nThemed<MCSelectorProperties> {
	private _propsInited: boolean = false;

	protected render(): DNode {
		const {
			quizstore,
			quizkind,
			progression,
			inreview = false,
			isaudit = false,
			// question properties
			hasNextQuestion,
			onNextQuestion,
			hasPreviousQuestion,
			onPreviousQuestion,
			onFinish,
			updater,
			onReview,
			getQuestions,
			filterOn,
			checkFor = () => true,
			hasNextSection = () => false,
			hasReviewed
		} = this.properties;
		const questionProps = {
			quizstore,
			hasNextQuestion,
			onNextQuestion,
			hasPreviousQuestion,
			onPreviousQuestion,
			onFinish,
			updater,
			onReview,
			hasReviewed
		} as MCQuestionProperties;

		const prometricAudit = (inreview: boolean) =>
			inreview
				? w(PrometricReview, {
						quizstore,
						getQuestions,
						onFinish,
						filterOn,
						checkFor,
						onReview,
						isaudit: true,
						localizedBundle: { isPlaceholder: true, format: () => '', messages: {} as Messages }
						// tslint:disable-next-line:indent
				  })
				: w(MCQuestionAudit, questionProps);

		const prometricQuiz = (inreview: boolean) =>
			inreview
				? w(PrometricReview, {
						quizstore,
						getQuestions,
						onFinish,
						filterOn,
						checkFor,
						onReview,
						isaudit: false,
						localizedBundle: { isPlaceholder: true, format: () => '', messages: {} as Messages }
						// tslint:disable-next-line:indent
				  })
				: w(MCQuestionPrometric, questionProps);

		const prometricMPEQuiz = (inreview: boolean) =>
			inreview
				? w(PrometricMPEReview, {
						quizstore,
						getQuestions,
						onFinish,
						filterOn,
						checkFor,
						onReview,
						hasNextSection,
						isaudit: false,
						localizedBundle: { isPlaceholder: true, format: () => '', messages: {} as Messages }
						// tslint:disable-next-line:indent
				  })
				: w(MCQuestionPrometric, questionProps);

		const pearsonvueAudit = (inreview: boolean) =>
			inreview
				? w(PearsonVueReview, {
						quizstore,
						getQuestions,
						onFinish,
						filterOn,
						checkFor,
						onReview,
						isaudit: true,
						localizedBundle: { isPlaceholder: true, format: () => '', messages: {} as Messages }
						// tslint:disable-next-line:indent
				  })
				: w(MCQuestionAudit, questionProps);

		const pearsonvueQuiz = (inreview: boolean) =>
			inreview
				? w(PearsonVueReview, {
						quizstore,
						getQuestions,
						onFinish,
						filterOn,
						checkFor,
						onReview,
						isaudit: false,
						localizedBundle: { isPlaceholder: true, format: () => '', messages: {} as Messages }
						// tslint:disable-next-line:indent
				  })
				: w(MCQuestionPearsonVue, questionProps);

		const qfAudit = (inreview: boolean) =>
			inreview
				? w(PretestReview, {
						quizstore,
						getQuestions,
						onFinish,
						filterOn,
						checkFor,
						onReview,
						isaudit: false,
						localizedBundle: { isPlaceholder: true, format: () => '', messages: {} as Messages }
						// tslint:disable-next-line:indent
				  })
				: w(MCQuestionAudit, questionProps);

		const pretestQuiz = () => w(MCQuestionPretest, questionProps);

		switch (quizkind) {
			case 'section':
				return isaudit ? qfAudit(inreview) : w(MCQuestionBase, questionProps);
			case 'pretest':
				return isaudit ? qfAudit(inreview) : pretestQuiz();
			case 'posttest':
			case 'practice':
				if (has('use-posttest-forward-progression')) {
					return isaudit ? qfAudit(inreview) : pretestQuiz();
				}
				switch (progression) {
					case 'prometric-mpe':
						return isaudit ? prometricAudit(inreview) : prometricMPEQuiz(inreview);
					case 'prometric':
						return isaudit ? prometricAudit(inreview) : prometricQuiz(inreview);
					case 'pearson-vue':
						return isaudit ? pearsonvueAudit(inreview) : pearsonvueQuiz(inreview);
					default:
						return isaudit ? qfAudit(inreview) : w(MCQuestionBase, questionProps);
				}
			default:
				return w(MCQuestionBase, questionProps);
		}
	}

	@beforeProperties()
	protected stateProperties(
		properties: MCSelectorProperties & WidgetProperties
	): MCSelectorProperties & WidgetProperties {
		const { quizstore } = properties;
		const { get, path } = quizstore;

		if (!this._propsInited) {
			const paths = [['quiz'], ['progression']];
			const invalidatePaths = paths.map((path: any) => quizstore.path(path.join('/')));
			const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
			this.own({
				destroy: () => {
					handle.remove();
				}
			});
			this._propsInited = true;
		}

		properties.quizkind = get(path('quiz')).kind;
		properties.progression = get(path('progression'));
		return properties;
	}
}

export default MCSelector;
